import { useTranslation } from "react-i18next";
import { PageLayout } from "../../Components/PageLayout";
import { usePaginatedQuery } from "../../Hooks/usePaginatedQuery";
import DataTable from "../../Components/DataTable/DataTable";
import { useState } from "react";
import { convertToPST } from "../../Helper/FormatDatetimes";
import { buildURLWithParams } from "../../Components/Utility/URLUtils";
import EditSeal from "./Modals/EditSeal";
import NewSeal from "./Modals/NewSeal";
import { TableNavigation } from "../../Components/Tables/Vehicles/TableNavigation";
import { useApiMutation } from "../../Hooks/useApiMutation";
import instance from "../../Interceptor/api_instance";
import { useNavigate } from "react-router-dom";
import { Spinner } from "../../Components/LoadingAnimations/LoadingAnimations";
import DebouncedInput from "../../Components/Inputs/DebouncedInput";

const DEFAULT_URL = "safety/seals/";

const sealsColumns = (t) => [
  {
    key: "sealNumber",
    header: t("yard-tables.table-columns.seal"),
    getValue: (row) => row.seal_number,
  },
  {
    key: "createdAt",
    header: t("yard-tables.table-columns.created_at"),
    getValue: (row) => convertToPST(row.created_at),
  },
  {
    key: "trailer",
    header: t("yard-tables.table-columns.trailer"),
    getValue: (row) =>
      row.check_in ? row.check_in.trailer.trailer_number : "N/A",
  },
  {
    key: "assignedBy",
    header: t("yard-tables.table-columns.employee"),
    getValue: (row) =>
      row.check_in
        ? row.check_in.employee
          ? row.check_in.employee.full_name
          : "N/A"
        : "N/A",
  },
  {
    key: "createdBy",
    header: t("forms.type.maintenance-report.labels.created-by"),
    getValue: (row) => row.created_by.full_name,
  },
];

/**
 * SealsView – paginated list of seals with filtering, inline editing, and creation.
 * Uses `usePaginatedQuery` for cursor-based pagination and `refetch` for post-mutation refreshes.
 */
const SealsView = () => {
  const { t } = useTranslation();

  // Paginated query: provides state (data, loading, pagination cursors), dispatch, and a reusable refetch helper
  const { state, dispatch, refetch } = usePaginatedQuery(DEFAULT_URL);

  // Modal visibility toggles
  const [newSeal, setNewSeal] = useState(false);
  const [selectedSeal, setSelectedSeal] = useState(null);

  const navigate = useNavigate();

  // Active filter values – kept in sync with the debounced input
  const [filters, setFilters] = useState({
    seal_number: "",
    start_date: null,
    end_date: null,
  });

  const {
    mutate: exportSeals,
    isLoading,
    error,
  } = useApiMutation(async ({ signal }) => {
    const response = await instance.post("/files/export-ctpat-f22/", filters, {
      signal,
    });
    if (response.status === 201) navigate("/export-jobs");
    else return response;
  });

  /**
   * handleFilter – updates filter state and resets pagination to page 1
   * so the user always sees the first page of filtered results.
   */
  const handleFilter = (name, value) => {
    const newFilters = {
      ...filters,
      [name]: value,
    };
    setFilters(newFilters);
    dispatch({
      type: "SET_CURRENT_PAGE",
      page: buildURLWithParams(DEFAULT_URL, newFilters),
      pageNum: 1,
    });
  };

  const columns = sealsColumns(t);
  return (
    <PageLayout title={"Seals"} header={t("seals")}>
      {/* New seal modal – on success, refetch the current filtered page to include the new record */}
      {newSeal && (
        <NewSeal
          onSuccess={() => refetch(buildURLWithParams(DEFAULT_URL, filters))}
          close={() => setNewSeal(false)}
        />
      )}

      {/* Edit seal modal – on success, patch the updated seal into the existing list without a network round-trip */}
      {selectedSeal && (
        <EditSeal
          seal={selectedSeal}
          close={() => setSelectedSeal(null)}
          onSuccess={(updatedSeal) => {
            const updatedData = state.data.map((seal) =>
              seal.id === updatedSeal.id ? updatedSeal : seal
            );
            dispatch({ type: "UPDATE_DATA", payload: updatedData });
          }}
        />
      )}
      {error && <p className="error">{error}</p>}
      <div className="search-filters-cont">
        <div className="search-filters">
          <label>
            {t("searchFilters.startDate")}
            {": "}
            <input
              type="date"
              className="text-input-sm"
              onChange={(e) => handleFilter("start_date", e.target.value)}
              value={filters.start_date || ""}
            />
          </label>
          <label>
            {t("searchFilters.endDate")}
            {": "}
            <input
              type="date"
              className="text-input-sm"
              onChange={(e) => handleFilter("end_date", e.target.value)}
              value={filters.end_date || ""}
            />
          </label>
          <DebouncedInput
            label={"seal"}
            name={"seal_number"}
            state={filters.seal_number}
            setState={handleFilter}
          />
          <button
            className="all-btn purple-btn"
            onClick={() => setNewSeal(true)}
          >
            {t("buttons.new")}
          </button>
          {isLoading ? (
            <Spinner />
          ) : (
            <button className="all-btn other-btn" onClick={exportSeals}>
              {t("buttons.export")}
            </button>
          )}
        </div>
        <TableNavigation state={state} dispatch={dispatch} />
      </div>
      <DataTable
        columns={columns}
        rows={state.data}
        isLoading={state.isLoading}
        onRowClick={setSelectedSeal}
      />
    </PageLayout>
  );
};

export default SealsView;
